import { MetadataType } from '@aibees/elephant/hooks';
import { SearchMatchType, SearchType } from '@aibees/elephant/types';
import { humanReadableSeniority } from '@aibees/elephant/utils';
import { Nil } from '@aibees/shared/definition';
import { Stack, Typography } from '@mui/material';
import {
  durationLabelParser,
  getDurationFilterFieldTags,
  jobRoleDepartmentLabelFormatter,
} from '../components/filter-tags/utils';
import {
  ClassificationGroups,
  ClassificationType,
  FilterBadgeType,
  FilterCategory,
  SearchFilter,
  SearchFilterBase,
  SearchFilterType,
} from '../types';
import { getJobRoleTreeOptions } from './job-functions';

export default function jobRoleFilterConfig(
  type: SearchType = SearchType.People
): SearchFilterBase {
  if (type === SearchType.Company) return companyJobRoleFilterConfig();

  return {
    id: 'Job Role',
    name: 'Job Role',
    icon: 'workspace',
    index: 25,
    type: SearchFilterType.ROOT,
    category: [FilterCategory.CONTACT_DATA],
    pinned: true,
    items: [
      {
        name: '',
        type: SearchFilterType.BUTTON_GROUP,
        icon: undefined,
        index: 0,
        key: '',
        field: '',
        metadata: { allowParallelItems: true },
        config: { labelKey: 'label', valueKey: 'value' },
        items: [
          {
            name: 'Applied Filters',
            key: 'job_role_applied_filters',
            field: 'job_role_applied_filters',
            type: SearchFilterType.APPLIED_FILTERS,
            index: 2,
            icon: undefined,
            metadata: {},
          },
          {
            name: 'Current Job',
            type: SearchFilterType.NONE,
            icon: { src: '/assets/icons/current-job.svg', sx: {} },
            index: 0,
            key: 'current_job',
            field: 'current_job',
            metadata: {},
            utils: {
              tags: {
                config: {
                  showAsCard: true,
                  tagsCardTitle: 'Current Job',
                },
              },
            },
            open: true,
            nested: true,
            mainItem: true,
            items: [
              {
                name: 'Primary active role only',
                type: SearchFilterType.SWITCH,
                index: 0,
                layer: 4,
                key: 'latest_active_job_title_only',
                field: 'latest_active_job_title_only',
                metadata: {
                  color: 'primary',
                  info: latestActiveJobTitleOnlyInfo(),
                  hideTags: true,
                },
              },
              {
                name: 'Job Title',
                type: SearchFilterType.NONE,
                icon: undefined,
                index: 0,
                key: 'current_job_job_title',
                field: 'current_job_job_title',
                metadata: { color: 'primary', singleEntry: false },
                config: { labelKey: 'label', valueKey: 'value' },
                nested: true,
                open: true,
                items: [
                  {
                    name: 'Include',
                    type: SearchFilterType.CUSTOM_SELECT,
                    icon: undefined,
                    index: 0,
                    exactMatch: {
                      enabled: true,
                      default: SearchMatchType.SMART,
                    },
                    key: 'current_job_include_job_title',
                    field: 'current_job_include_job_title',
                    metadata: {
                      color: 'primary',
                      icon: 'add-circle-outline',
                      placeholder: 'Current Job Title',
                      preset: 'current_job_job_title',
                      jobTitleExpanderVisible: true,
                      advancedButtonVisible: true,
                      dialog: {
                        title: 'Bulk Include Job Role',
                        subTitleItemLabel: 'Job Title',
                        subTitleItemLabelPlural: 'Job Titles',
                        textArea: {
                          placeholder: 'Product Design, CTO, CSO, Founder, ...',
                        },
                      },
                      autocomplete: {
                        recent: false,
                        checkboxPosition: 'left',
                        multiple: true,
                        transformSearchValue: (v: string) => v.toLowerCase(),
                        allowCustomValuesByEnter: true,
                      },
                    },
                    utils: {
                      tags: { labelFormatter: humanReadableJobRole },
                    },
                    open: true,
                    searchKey: 'value',
                    config: { labelKey: 'value', valueKey: 'value' },
                    options: [
                      {
                        type: 'URL',
                        value: `/elephant-management/um/classifications?size=10&page=0&group=${ClassificationGroups.PERSON}&key=${ClassificationType.HEADLINE}`,
                        label: '',
                        icon: null,
                        index: undefined,
                      },
                    ],
                  },
                  {
                    name: 'Exclude',
                    type: SearchFilterType.CUSTOM_SELECT,
                    icon: undefined,
                    index: 0,
                    exactMatch: {
                      enabled: true,
                      default: SearchMatchType.SMART,
                    },
                    key: 'current_job_exclude_job_title',
                    field: 'current_job_exclude_job_title',
                    metadata: {
                      color: 'error',
                      placeholder: 'Current Job Title',
                      icon: 'block',
                      preset: 'current_job_job_title',
                      jobTitleExpanderVisible: true,
                      advancedButtonVisible: true,
                      dialog: {
                        title: 'Bulk Exclude Job Role',
                        subTitleItemLabel: 'Job Title',
                        subTitleItemLabelPlural: 'Job Titles',
                        textArea: {
                          placeholder: 'Product Design, CTO, CSO, Founder, ...',
                        },
                      },
                      autocomplete: {
                        recent: false,
                        checkboxPosition: 'left',
                        multiple: true,
                        transformSearchValue: (v: string) => v.toLowerCase(),
                        allowCustomValuesByEnter: true,
                      },
                    },
                    utils: {
                      tags: { labelFormatter: humanReadableJobRole },
                    },
                    searchKey: 'value',
                    config: { labelKey: 'value', valueKey: 'value' },
                    options: [
                      {
                        type: 'URL',
                        value: `/elephant-management/um/classifications?size=10&page=0&group=${ClassificationGroups.PERSON}&key=${ClassificationType.HEADLINE}`,
                        label: '',
                        icon: null,
                        index: undefined,
                      },
                    ],
                  },
                  {
                    name: 'Include (AND)',
                    type: SearchFilterType.CUSTOM_SELECT,
                    icon: undefined,
                    index: 0,
                    exactMatch: {
                      enabled: true,
                      default: SearchMatchType.SMART,
                    },
                    key: 'current_job_all_of_job_title',
                    field: 'current_job_all_of_job_title',
                    metadata: {
                      color: 'warning',
                      icon: 'add-task',
                      placeholder: 'Current Job Title',
                      preset: 'current_job_job_title',
                      jobTitleExpanderVisible: true,
                      advancedButtonVisible: true,
                      dialog: {
                        title: 'Bulk Include (AND) Job Role',
                        subTitleItemLabel: 'Job Title',
                        subTitleItemLabelPlural: 'Job Titles',
                        textArea: {
                          placeholder: 'Product Design, CTO, CSO, Founder, ...',
                        },
                      },
                      autocomplete: {
                        recent: false,
                        checkboxPosition: 'left',
                        multiple: true,
                        transformSearchValue: (v: string) => v.toLowerCase(),
                        allowCustomValuesByEnter: true,
                      },
                    },
                    utils: {
                      tags: { labelFormatter: humanReadableJobRole },
                    },
                    searchKey: 'value',
                    config: { labelKey: 'value', valueKey: 'value' },
                    options: [
                      {
                        type: 'URL',
                        value: `/elephant-management/um/classifications?size=10&page=0&group=${ClassificationGroups.PERSON}&key=${ClassificationType.HEADLINE}`,
                        label: '',
                        icon: null,
                        index: undefined,
                      },
                    ],
                  },
                ],
              },
              {
                name: 'Organizational Roles',
                type: SearchFilterType.ACCORDION_SECTION,
                icon: 'lan-outline',
                index: 1,
                key: 'current_job_role',
                field: 'current_job_role',
                nested: true,
                open: true,
                items: [
                  {
                    name: 'Seniority',
                    type: SearchFilterType.CUSTOM_SELECT,
                    icon: undefined,
                    index: 0,
                    key: 'current_job_role_seniority',
                    field: 'current_job_role_seniority',
                    metadata: {
                      color: 'primary',
                      placeholder: 'Management Level',
                      delimiter: ':',
                      autocomplete: {
                        checkboxPosition: 'left',
                        multiple: true,
                      },
                      metaDataApi: {
                        key: MetadataType.PERSON_SENIORITY,
                        enabled: true,
                      },
                    },
                    config: { labelKey: 'value', valueKey: 'value' },
                    nested: true,
                    searchKey: 'value',
                    utils: {
                      tags: { labelFormatter: humanReadableSeniority },
                    },
                    options: [
                      {
                        type: 'URL',
                        value: `/elephant-management/um/classifications?size=25&page=0&group=${ClassificationGroups.PERSON}&key=${ClassificationType.SENIORITY}`,
                        label: '',
                        icon: null,
                        index: undefined,
                      },
                    ],
                  },
                  currentJobRoleDepartmentFilterConfig(),
                ],
              },
              {
                name: 'Experience',
                type: SearchFilterType.ACCORDION_SECTION,
                icon: 'work-outline',
                index: 1,
                key: 'current_job_experience',
                field: 'current_job_experience',
                nested: true,
                open: true,
                items: [
                  {
                    name: 'Time in Current Job Role',
                    type: SearchFilterType.DURATION,
                    icon: undefined,
                    index: 0,
                    key: 'current_job_experience_current_job',
                    field: 'current_job_experience_current_job',
                    metadata: {
                      color: 'primary',
                      info: <DurationFilterInfo />,
                    },
                    nested: false,
                    utils: {
                      tags: {
                        getFilterItemTagFunc: getDurationFilterFieldTags,
                        labelFormatter: durationLabelParser,
                      },
                    },
                  },
                  {
                    name: 'Time in Current Company',
                    type: SearchFilterType.DURATION,
                    icon: undefined,
                    index: 0,
                    key: 'current_job_experience_current_company',
                    field: 'current_job_experience_current_company',
                    metadata: {
                      color: 'primary',
                      info: <DurationFilterInfo />,
                    },
                    nested: false,
                    utils: {
                      tags: {
                        labelFormatter: durationLabelParser,
                      },
                    },
                  },
                  {
                    name: 'Time in Work Experience',
                    type: SearchFilterType.DURATION,
                    icon: undefined,
                    index: 0,
                    key: 'current_job_experience_time',
                    field: 'current_job_experience_time',
                    metadata: {
                      color: 'primary',
                      info: <DurationFilterInfo />,
                    },
                    nested: false,
                    utils: {
                      tags: {
                        labelFormatter: durationLabelParser,
                      },
                    },
                  },
                ],
              },
            ],
          },
          {
            name: 'Previous Job',
            type: SearchFilterType.NONE,
            icon: { src: '/assets/icons/prev-job.svg', sx: {} },
            index: 0,
            key: 'prev_job',
            field: 'prev_job',
            metadata: {},
            utils: {
              tags: {
                config: {
                  showAsCard: true,
                  tagsCardTitle: 'Previous Job',
                },
              },
            },
            open: true,
            nested: true,
            mainItem: true,
            items: [
              {
                name: 'Job Title',
                type: SearchFilterType.NONE,
                icon: undefined,
                index: 0,
                key: 'prev_job_job_title',
                field: 'prev_job_job_title',
                metadata: { color: 'primary', singleEntry: false },
                config: { labelKey: 'label', valueKey: 'value' },
                nested: true,
                open: true,
                sections: { main: 3, more: 1 },
                items: [
                  {
                    name: 'Include',
                    type: SearchFilterType.CUSTOM_SELECT,
                    icon: undefined,
                    index: 0,
                    exactMatch: {
                      enabled: true,
                      default: SearchMatchType.SMART,
                    },
                    key: 'prev_job_include_job_title',
                    field: 'prev_job_include_job_title',
                    metadata: {
                      color: 'primary',
                      icon: 'add-circle-outline',
                      placeholder: 'Previous Job Title',
                      preset: 'current_job_job_title',
                      advancedButtonVisible: true,
                      dialog: {
                        title: 'Bulk Include Job Role',
                        subTitleItemLabel: 'Job Title',
                        subTitleItemLabelPlural: 'Job Titles',
                        textArea: {
                          placeholder: 'Product Design, CTO, CSO, Founder, ...',
                        },
                      },
                      autocomplete: {
                        recent: false,
                        checkboxPosition: 'left',
                        multiple: true,
                        transformSearchValue: (v: string) => v.toLowerCase(),
                        allowCustomValuesByEnter: true,
                      },
                    },
                    utils: {
                      tags: { labelFormatter: humanReadableJobRole },
                    },
                    open: true,
                    searchKey: 'value',
                    config: { labelKey: 'value', valueKey: 'value' },
                    options: [
                      {
                        type: 'URL',
                        value: `/elephant-management/um/classifications?size=10&page=0&group=${ClassificationGroups.PERSON}&key=${ClassificationType.HEADLINE}`,
                        label: '',
                        icon: null,
                        index: undefined,
                      },
                    ],
                  },
                  {
                    name: 'Exclude',
                    type: SearchFilterType.CUSTOM_SELECT,
                    icon: undefined,
                    index: 0,
                    exactMatch: {
                      enabled: true,
                      default: SearchMatchType.SMART,
                    },
                    key: 'prev_job_exclude_job_title',
                    field: 'prev_job_exclude_job_title',
                    metadata: {
                      color: 'error',
                      placeholder: 'Previous Job Title',
                      icon: 'block',
                      preset: 'current_job_job_title',
                      advancedButtonVisible: true,
                      dialog: {
                        title: 'Bulk Exclude Job Role',
                        subTitleItemLabel: 'Job Title',
                        subTitleItemLabelPlural: 'Job Titles',
                        textArea: {
                          placeholder: 'Product Design, CTO, CSO, Founder, ...',
                        },
                      },
                      autocomplete: {
                        recent: false,
                        checkboxPosition: 'left',
                        multiple: true,
                        transformSearchValue: (v: string) => v.toLowerCase(),
                        allowCustomValuesByEnter: true,
                      },
                    },
                    utils: {
                      tags: { labelFormatter: humanReadableJobRole },
                    },
                    searchKey: 'value',
                    config: { labelKey: 'value', valueKey: 'value' },
                    options: [
                      {
                        type: 'URL',
                        value: `/elephant-management/um/classifications?size=10&page=0&group=${ClassificationGroups.PERSON}&key=${ClassificationType.HEADLINE}`,
                        label: '',
                        icon: null,
                        index: undefined,
                      },
                    ],
                  },
                  {
                    name: 'Include (AND)',
                    type: SearchFilterType.CUSTOM_SELECT,
                    icon: undefined,
                    index: 0,
                    exactMatch: {
                      enabled: true,
                      default: SearchMatchType.SMART,
                    },
                    key: 'prev_job_all_of_job_title',
                    field: 'prev_job_all_of_job_title',
                    metadata: {
                      color: 'warning',
                      icon: 'add-task',
                      placeholder: 'Previous Job Title',
                      preset: 'current_job_job_title',
                      advancedButtonVisible: true,
                      dialog: {
                        title: 'Bulk Include (AND) Job Role',
                        subTitleItemLabel: 'Job Title',
                        subTitleItemLabelPlural: 'Job Titles',
                        textArea: {
                          placeholder: 'Product Design, CTO, CSO, Founder, ...',
                        },
                      },
                      autocomplete: {
                        recent: false,
                        checkboxPosition: 'left',
                        multiple: true,
                        transformSearchValue: (v: string) => v.toLowerCase(),
                        allowCustomValuesByEnter: true,
                      },
                    },
                    utils: {
                      tags: { labelFormatter: humanReadableJobRole },
                    },
                    searchKey: 'value',
                    config: { labelKey: 'value', valueKey: 'value' },
                    options: [
                      {
                        type: 'URL',
                        value: `/elephant-management/um/classifications?size=10&page=0&group=${ClassificationGroups.PERSON}&key=${ClassificationType.HEADLINE}`,
                        label: '',
                        icon: null,
                        index: undefined,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };
}

/**
 * Company-search variant of the Job Role filter. Differs from the People
 * variant: no Current/Previous tabs (a single "Job Title" section), no
 * Experience section, and no "Primary active role only" switch. Its values are
 * sent under `account.employee.{title,seniority,departmentAndFunction}` —
 * see the transform in use-browse-company-search.ts.
 */
function companyJobRoleFilterConfig(): SearchFilterBase {
  return {
    id: 'Job Role',
    name: 'Employee Role',
    icon: 'workspace',
    index: 25,
    type: SearchFilterType.ROOT,
    category: [FilterCategory.CONTACT_DATA],
    pinned: true,
    badge: FilterBadgeType.NEW,
    forcePinnedAt: '2026-07-10T14:32:06.000Z',
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'Employee Role',
        },
      },
    },
    items: [
      {
        name: '',
        type: SearchFilterType.MIXED,
        index: 1,
        key: 'company_job_role_container',
        field: 'company_job_role_container',
        nested: true,
        items: [
          {
            name: 'Job Title',
            type: SearchFilterType.NONE,
            icon: undefined,
            index: 0,
            key: 'company_job_role_job_title',
            field: 'company_job_role_job_title',
            metadata: { color: 'primary', singleEntry: false },
            config: { labelKey: 'label', valueKey: 'value' },
            nested: true,
            open: true,
            items: [
              companyJobTitleItem('include'),
              companyJobTitleItem('exclude'),
              companyJobTitleItem('all'),
            ],
          },
          {
            name: 'Organizational Roles',
            type: SearchFilterType.ACCORDION_SECTION,
            icon: 'lan-outline',
            index: 1,
            key: 'company_job_role_role',
            field: 'company_job_role_role',
            nested: true,
            open: true,
            items: [
              {
                name: 'Seniority',
                type: SearchFilterType.CUSTOM_SELECT,
                icon: undefined,
                index: 0,
                key: 'company_job_role_seniority',
                field: 'company_job_role_seniority',
                metadata: {
                  color: 'primary',
                  placeholder: 'Management Level',
                  delimiter: ':',
                  autocomplete: {
                    checkboxPosition: 'left',
                    multiple: true,
                  },
                  metaDataApi: {
                    key: MetadataType.COMPANY_EMPLOYEE_SENIORITY,
                    enabled: true,
                  },
                },
                config: { labelKey: 'value', valueKey: 'value' },
                nested: true,
                searchKey: 'value',
                utils: {
                  tags: { labelFormatter: humanReadableSeniority },
                },
                options: [
                  {
                    type: 'URL',
                    value: `/elephant-management/um/classifications?size=25&page=0&group=${ClassificationGroups.PERSON}&key=${ClassificationType.SENIORITY}`,
                    label: '',
                    icon: null,
                    index: undefined,
                  },
                ],
              },
              companyJobRoleDepartmentFilterConfig(),
            ],
          },
        ],
      },
    ],
  };
}

// Builds one of the three company Job Title autocompletes (Include / Exclude /
// Include (AND)) — mirrors the People current/previous Job Title items.
function companyJobTitleItem(
  variant: 'include' | 'exclude' | 'all'
): SearchFilter {
  const variants = {
    include: {
      name: 'Include',
      key: 'company_job_role_include_job_title',
      color: 'primary',
      icon: 'add-circle-outline',
      dialogTitle: 'Bulk Include Employee Role',
      open: true,
    },
    exclude: {
      name: 'Exclude',
      key: 'company_job_role_exclude_job_title',
      color: 'error',
      icon: 'block',
      dialogTitle: 'Bulk Exclude Employee Role',
      open: false,
    },
    all: {
      name: 'Include (AND)',
      key: 'company_job_role_all_of_job_title',
      color: 'warning',
      icon: 'add-task',
      dialogTitle: 'Bulk Include (AND) Employee Role',
      open: false,
    },
  } as const;
  const cfg = variants[variant];

  return {
    name: cfg.name,
    type: SearchFilterType.CUSTOM_SELECT,
    icon: undefined,
    index: 0,
    exactMatch: {
      enabled: true,
      default: SearchMatchType.SMART,
    },
    key: cfg.key,
    field: cfg.key,
    metadata: {
      color: cfg.color,
      icon: cfg.icon,
      placeholder: 'Employee Job Title',
      preset: 'company_job_role_job_title',
      jobTitleExpanderVisible: true,
      advancedButtonVisible: true,
      dialog: {
        title: cfg.dialogTitle,
        subTitleItemLabel: 'Job Title',
        subTitleItemLabelPlural: 'Job Titles',
        textArea: {
          placeholder: 'Product Design, CTO, CSO, Founder, ...',
        },
      },
      autocomplete: {
        recent: false,
        checkboxPosition: 'left',
        multiple: true,
        transformSearchValue: (v: string) => v.toLowerCase(),
        allowCustomValuesByEnter: true,
      },
    },
    utils: {
      tags: { labelFormatter: humanReadableJobRole },
    },
    ...(cfg.open ? { open: true } : {}),
    searchKey: 'value',
    config: { labelKey: 'value', valueKey: 'value' },
    options: [
      {
        type: 'URL',
        value: `/elephant-management/um/classifications?size=10&page=0&group=${ClassificationGroups.PERSON}&key=${ClassificationType.HEADLINE}`,
        label: '',
        icon: null,
        index: undefined,
      },
    ],
  };
}

function companyJobRoleDepartmentFilterConfig(): SearchFilter {
  return {
    name: 'Department & Job function',
    type: SearchFilterType.CUSTOM_SELECT_TREE,
    icon: undefined,
    index: 0,
    key: 'company_job_role_department',
    field: 'company_job_role_department',
    metadata: {
      color: 'primary',
      placeholder: 'Search Dep. & Job functions',
      preset: 'company_job_role_department',
      delimiter: '::',
      autocomplete: {
        recent: false,
        checkboxPosition: 'right',
        multiple: true,
        customOptions: true,
      },
    },
    utils: {
      tags: {
        labelFormatter: jobRoleDepartmentLabelFormatter,
      },
    },
    config: { labelKey: 'label', valueKey: 'value' },
    nested: true,
    treeOptions: getJobRoleTreeOptions(),
  };
}

export function currentJobRoleDepartmentFilterConfig(): SearchFilter {
  return {
    name: 'Department & Job function',
    type: SearchFilterType.CUSTOM_SELECT_TREE,
    icon: undefined,
    index: 0,
    key: 'current_job_role_department',
    field: 'current_job_role_department',
    metadata: {
      color: 'primary',
      placeholder: 'Search Dep. & Job functions',
      preset: 'current_job_role_department',
      delimiter: '::',
      autocomplete: {
        recent: false,
        checkboxPosition: 'right',
        multiple: true,
        customOptions: true,
      },
    },
    utils: {
      tags: {
        labelFormatter: jobRoleDepartmentLabelFormatter,
      },
    },
    config: { labelKey: 'label', valueKey: 'value' },
    nested: true,
    treeOptions: getJobRoleTreeOptions(),
  };
}

function DurationFilterInfo() {
  return (
    <Stack direction="column" gap={0.25}>
      <Typography variant="body5" fontWeight={700}>
        Data updates monthly on the 1st.
      </Typography>
      <Typography variant="body5">
        <b>1 month</b> → current month only, as of the last refresh (no
        mid-month updates).
      </Typography>
      <Typography variant="body5">
        <b>2 months</b> → current + previous month.
      </Typography>
      <Typography variant="body5" fontStyle="italic">
        Example: On Nov 29, '1 month' returns data as it was on Nov 1.Choose 2
        months to also include October.
      </Typography>
    </Stack>
  );
}

export function latestActiveJobTitleOnlyInfo() {
  return (
    <Stack direction="column" gap={1} sx={{ maxWidth: (p) => p.spacing(25) }}>
      <Typography variant="body5">
        Only matches if the job title is the most recent active entry on the
        profile.
      </Typography>
      <Typography variant="body5">
        This filters out secondary concurrent roles (e.g. Advisor, Board Member)
        that are not the person's primary focus.
      </Typography>
    </Stack>
  );
}

export function humanReadableJobRole(value: Nil<string>) {
  if (!value) return '';
  return (
    value
      ?.toLowerCase()
      // .replace(/[-_]/g, ' ')
      .replace(/\b[a-z]/g, (letter) => {
        return letter.toUpperCase();
      })
  );
}
